import { pxToInt, px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, InlineEditDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

const BUTTON_SIZE = {
  height: '24px',
  width: '24px',
} as const;

export const getInlineEditTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): InlineEditDesignTokens => {
  const { tokens, typography, borderRadius, spacing } = theme;
  const { colors } = tokens;

  return {
    container: {
      _: {
        'font-family': typography.labelMedium.fontFamily,
        'font-weight': String(typography.labelMedium.fontWeight),
        'font-size': typography.labelMedium.fontSize,
        'line-height': typography.labelMedium.lineHeight,
        'align-items': 'center',
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': 'transparent',
        cursor: 'pointer',
        display: 'flex',
        'min-height': '36px',
        'max-width': '100%',
        position: 'relative',
        width: 'fit-content',
        'text-align': 'left',
      },
      disabled: {
        true: {
          cursor: 'default',
          background: colors.background.primary.value,
          color: colors.text.primary.value,
          'user-select': 'text',
        },
        false: {},
      },
      editing: {
        true: { 'border-radius': px(borderRadius.small + 1) },
        false: {
          'border-radius': px(borderRadius.small),
          'padding-top': px(spacing.XXS),
          'padding-right': px(spacing.XS),
          'padding-bottom': px(spacing.XXS),
          'padding-left': px(spacing.S),
        },
      },
      /**
       * Marker variant only — error styling lives in `compoundVariants`
       * gated on `disabled: 'false'` so the error border doesn't paint
       * over the disabled state.
       */
      error: { true: {}, false: {} },
      compoundVariants: [
        {
          disabled: 'false',
          editing: 'false',
          styles: {
            background: colors.background.primary.value,
            '&:hover': {
              'border-width': '1px',
              'border-style': 'dashed',
              'border-color': colors.border.hover.dropzone.value,
              '& svg': {
                color: colors.icon.hover.value,
                visibility: 'visible',
              },
            },
            '&:active': {
              'border-width': '1px',
              'border-style': 'dashed',
              'border-color': colors.border.active.value,
              '& svg': {
                color: colors.icon.pressed.value,
              },
            },
          },
        },
        {
          disabled: 'false',
          editing: 'true',
          styles: {
            background: colors.background.primary.value,
            'border-color': colors.border.selected.value,
            cursor: 'default',
            width: '100%',
          },
        },
        {
          disabled: 'false',
          error: 'true',
          styles: {
            'border-color': colors.border.error.value,
          },
        },
      ],
    },
    value: {
      _: {
        // Allow consumer to set custom typography
        'font-family': 'inherit',
        'font-weight': 'inherit',
        'font-size': 'inherit',
        'line-height': 'inherit',
        color: colors.text.primary.value,
        overflow: 'hidden',
        'text-overflow': 'ellipsis',
        width: '100%',
        'user-select': 'text',
      },
      placeholder: {
        true: { color: colors.text.tertiary.value },
        false: {},
      },
    },
    icon: {
      _: {
        'margin-left': px(spacing.S),
        'pointer-events': 'none',
        color: colors.icon.secondary.value,
        'flex-shrink': 0,
      },
      showPencilOnHoverOnly: {
        true: { visibility: 'hidden' },
        false: {},
      },
    },
    inputWrapper: {
      _: {
        'font-family': typography.labelMedium.fontFamily,
        'font-weight': String(typography.labelMedium.fontWeight),
        'font-size': typography.labelMedium.fontSize,
        'line-height': typography.labelMedium.lineHeight,
        'align-items': 'center',
        display: 'flex',
        'margin-top': px(spacing.XXS),
        'margin-right': px(spacing.XS),
        'margin-bottom': px(spacing.XXS),
        'margin-left': px(spacing.S),
        width: '100%',
      },
    },
    input: {
      _: {
        'font-family': 'inherit',
        'font-weight': 'inherit',
        'font-size': 'inherit',
        'line-height': 'inherit',
        background: colors.background.primary.value,
        border: 'none',
        color: colors.text.primary.value,
        'margin-left': `-${px(spacing.XXXS)}`,
        padding: 0,
        outline: 'none',
        width: '100%',
        'box-sizing': 'content-box',
        '&::placeholder': {
          color: colors.text.tertiary.value,
        },
      },
    },
    characterCount: {
      color: 'inherit',
      'margin-left': '6px',
      'flex-shrink': 0,
    },
    editButton: {
      cancel: {
        ...BUTTON_SIZE,
        'margin-top': '0',
        'margin-bottom': '0',
        'margin-right': px(spacing.XXS),
        'margin-left': px(spacing.XXS),
        '& svg': { color: colors.icon.link.value },
      },
      submit: {
        _: { ...BUTTON_SIZE },
        inactive: { '& svg': { color: colors.icon.link.value } },
      },
    },
  };
};

export const getInlineEditTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): InlineEditDesignTokens => {
  const input = tokens?.component?.input;
  const generic = tokens?.generic;
  const semantic = tokens?.semantic;

  const inputBackground = semantic?.['background-color']?.surface?.[100];
  const inputTextColor = semantic?.['text-color']?.medium?.default;
  const placeholderTextColor = semantic?.['text-color']?.light;
  const errorBorderColor = input?.error?.['border-bottom-color'];
  const focusBorderColor = input?.['border-bottom-color']?.focus;
  const hoverBorderColor = input?.['border-bottom-color']?.hover;
  const pressedBorderColor = semantic?.['border-color']?.divider?.heavy;
  const iconColorIdle = semantic?.['icon-color']?.medium;
  const iconColorHover = semantic?.['icon-color']?.control?.hover;
  const iconColorPressed = semantic?.['icon-color']?.control?.pressed;
  const iconColorLink = semantic?.['icon-color']?.link;

  const borderRTL = pxToInt(input?.['border-top-left-radius']);
  const borderRTR = pxToInt(input?.['border-top-right-radius']);

  const editingBorderRTL = px(borderRTL + 1);
  const editingBorderRTR = px(borderRTR + 1);

  return {
    container: {
      _: {
        'font-family': fontFamily,
        'font-weight': input?.['font-weight'],
        'font-size': input?.['font-size'],
        'line-height': input?.['line-height'],
        'align-items': 'center',
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': 'transparent',
        cursor: 'pointer',
        display: 'flex',
        'min-height': '36px',
        'max-width': '100%',
        position: 'relative',
        width: 'fit-content',
        'text-align': 'left',
      },
      disabled: {
        true: {
          cursor: 'default',
          background: inputBackground,
          color: inputTextColor,
          'user-select': 'text',
        },
        false: {},
      },
      editing: {
        true: {
          'border-top-left-radius': editingBorderRTL,
          'border-top-right-radius': editingBorderRTR,
        },
        false: {
          'border-top-left-radius': px(borderRTL),
          'border-top-right-radius': px(borderRTR),
          'padding-top': generic?.spacing?.[4],
          'padding-right': generic?.spacing?.[8],
          'padding-bottom': generic?.spacing?.[4],
          'padding-left': generic?.spacing?.[12],
        },
      },
      /**
       * Marker variant only — error styling lives in `compoundVariants`
       * gated on `disabled: 'false'` so the error border doesn't paint
       * over the disabled state.
       */
      error: { true: {}, false: {} },
      compoundVariants: [
        {
          disabled: 'false',
          editing: 'false',
          styles: {
            background: inputBackground,
            '&:hover': {
              'border-width': '1px',
              'border-style': 'dashed',
              'border-color': hoverBorderColor,
              '& svg': {
                color: iconColorHover,
                visibility: 'visible',
              },
            },
            '&:active': {
              'border-width': '1px',
              'border-style': 'dashed',
              'border-color': pressedBorderColor,
              '& svg': {
                color: iconColorPressed,
              },
            },
          },
        },
        {
          disabled: 'false',
          editing: 'true',
          styles: {
            background: inputBackground,
            'border-color': focusBorderColor,
            cursor: 'default',
            width: '100%',
          },
        },
        {
          disabled: 'false',
          error: 'true',
          styles: {
            'border-color': errorBorderColor,
          },
        },
      ],
    },
    value: {
      _: {
        // Allow consumer to set custom typography
        'font-family': 'inherit',
        'font-weight': 'inherit',
        'font-size': 'inherit',
        'line-height': 'inherit',
        color: inputTextColor,
        overflow: 'hidden',
        'text-overflow': 'ellipsis',
        width: '100%',
        'user-select': 'text',
      },
      placeholder: {
        true: { color: placeholderTextColor },
        false: {},
      },
    },
    icon: {
      _: {
        'margin-left': generic?.spacing?.[12],
        'pointer-events': 'none',
        color: iconColorIdle,
        'flex-shrink': 0,
      },
      showPencilOnHoverOnly: {
        true: { visibility: 'hidden' },
        false: {},
      },
    },
    inputWrapper: {
      _: {
        'font-family': fontFamily,
        'font-weight': input?.['font-weight'],
        'font-size': input?.['font-size'],
        'line-height': input?.['line-height'],
        'align-items': 'center',
        display: 'flex',
        'margin-top': generic?.spacing?.[4],
        'margin-right': generic?.spacing?.[8],
        'margin-bottom': generic?.spacing?.[4],
        'margin-left': generic?.spacing?.[12],
        width: '100%',
      },
    },
    input: {
      _: {
        'font-family': 'inherit',
        'font-weight': 'inherit',
        'font-size': 'inherit',
        'line-height': 'inherit',
        background: inputBackground,
        border: 'none',
        color: inputTextColor,
        'margin-left': `-${generic?.spacing?.[2]}`,
        padding: 0,
        outline: 'none',
        width: '100%',
        'box-sizing': 'content-box',
        '&::placeholder': {
          color: placeholderTextColor,
        },
      },
    },
    characterCount: {
      color: 'inherit',
      'margin-left': '6px',
      'flex-shrink': 0,
    },
    editButton: {
      cancel: {
        ...BUTTON_SIZE,
        'margin-top': '0',
        'margin-bottom': '0',
        'margin-right': generic?.spacing?.[4],
        'margin-left': generic?.spacing?.[4],
        '& svg': { color: iconColorLink },
      },
      submit: {
        _: { ...BUTTON_SIZE },
        inactive: { '& svg': { color: iconColorLink } },
      },
    },
  };
};
